import {PlatformFieldDataSelectOption} from './types'

/** Saved native ORDER config row from GET /oms/orders/{id} → data.order_data[] */
export type OrderDataRow = {
  id?: number
  platform_field: number
  platform_field_data: number
  type?: string
  api_id?: string
  api_name?: string
}

export const ORDER_DATA_TYPES = {
  CHANNEL: 'CHANNEL',
  SHIPPINGPROVIDER: 'SHIPPINGPROVIDER',
  PAYMENT_METHOD: 'PAYMENT_METHOD',
  ORDER_STATUS: 'ORDER_STATUS',
  LOCATION: 'LOCATION',
  TAXCODE: 'TAXCODE',
} as const

export type OrderDataType = (typeof ORDER_DATA_TYPES)[keyof typeof ORDER_DATA_TYPES]

export const normalizeOrderDataRows = (orderData: unknown): OrderDataRow[] => {
  if (!Array.isArray(orderData)) return []
  return orderData.filter((row) => row && typeof row === 'object') as OrderDataRow[]
}

/** Find saved row by type, optionally falling back to platform_field id */
export const getOrderDataRow = (
  orderData: OrderDataRow[] | unknown,
  type: OrderDataType,
  platformFieldId?: number | null
): OrderDataRow | undefined => {
  const rows = normalizeOrderDataRows(orderData)
  const byType = rows.find((row) => row.type === type)
  if (byType) return byType
  if (platformFieldId != null) {
    return rows.find((row) => Number(row.platform_field) === Number(platformFieldId))
  }
  return undefined
}

/** platform_field_data id as string — matches OmsPlatformFieldDataSelect value */
export const getOrderDataValue = (
  orderData: OrderDataRow[] | unknown,
  type: OrderDataType,
  platformFieldId?: number | null
): string => {
  const row = getOrderDataRow(orderData, type, platformFieldId)
  if (!row) return ''
  if (row.platform_field_data != null) return String(row.platform_field_data)
  if (row.api_id) return String(row.api_id)
  return ''
}

/** platform_field id from saved row — use as fieldId for platform-field-data API */
export const getOrderDataFieldId = (
  orderData: OrderDataRow[] | unknown,
  type: OrderDataType
): number | null => {
  const row = getOrderDataRow(orderData, type)
  const fieldId = row?.platform_field
  return fieldId != null ? Number(fieldId) : null
}

/** Optional seed label for select when order_data.api_name is present */
export const getOrderDataSeedOption = (
  orderData: OrderDataRow[] | unknown,
  type: OrderDataType
): PlatformFieldDataSelectOption | null => {
  const row = getOrderDataRow(orderData, type)
  const value = getOrderDataValue(orderData, type)
  const label = row?.api_name?.trim()
  if (!value || !label) return null
  return {value, label}
}

export type HydratedOrderConfig = {
  channelSource: string
  shippingMethod: string
  paymentMethod: string
  orderStatus: string
  location: string
  taxCode: string
  fieldIds: {
    channel: number | null
    shipping: number | null
    payment: number | null
    orderStatus: number | null
    location: number | null
    taxCode: number | null
  }
  rows: OrderDataRow[]
}

export const hydrateOrderConfigFromOrderData = (orderData: unknown): HydratedOrderConfig => {
  const rows = normalizeOrderDataRows(orderData)

  return {
    channelSource: getOrderDataValue(rows, ORDER_DATA_TYPES.CHANNEL),
    shippingMethod: getOrderDataValue(rows, ORDER_DATA_TYPES.SHIPPINGPROVIDER),
    paymentMethod: getOrderDataValue(rows, ORDER_DATA_TYPES.PAYMENT_METHOD),
    orderStatus: getOrderDataValue(rows, ORDER_DATA_TYPES.ORDER_STATUS),
    location: getOrderDataValue(rows, ORDER_DATA_TYPES.LOCATION),
    taxCode: getOrderDataValue(rows, ORDER_DATA_TYPES.TAXCODE),
    fieldIds: {
      channel: getOrderDataFieldId(rows, ORDER_DATA_TYPES.CHANNEL),
      shipping: getOrderDataFieldId(rows, ORDER_DATA_TYPES.SHIPPINGPROVIDER),
      payment: getOrderDataFieldId(rows, ORDER_DATA_TYPES.PAYMENT_METHOD),
      orderStatus: getOrderDataFieldId(rows, ORDER_DATA_TYPES.ORDER_STATUS),
      location: getOrderDataFieldId(rows, ORDER_DATA_TYPES.LOCATION),
      taxCode: getOrderDataFieldId(rows, ORDER_DATA_TYPES.TAXCODE),
    },
    rows,
  }
}

const parseCheckboxValue = (input: any): boolean => {
  if (typeof input === 'boolean') return input
  if (typeof input === 'number') return input === 1
  if (typeof input === 'string') {
    const normalized = input.trim().toLowerCase()
    return normalized === 'true' || normalized === '1' || normalized === 'yes'
  }
  if (input && typeof input === 'object') {
    const raw =
      input?.value ??
      input?.name ??
      input?.label ??
      input?.api_name ??
      input?.field_value
    return parseCheckboxValue(raw)
  }
  return false
}

export const isEmptyRequiredValue = (type: string, val: any) => {
  switch (type) {
    case 'MULTISELECT':
      return !Array.isArray(val) || val.length === 0
    case 'SELECT':
      return val === '' || val === null || val === undefined
    case 'CHECKBOX':
      return parseCheckboxValue(val) !== true
    default:
      if (val === null || val === undefined) return true
      if (typeof val === 'string') return val.trim() === ''
      return false
  }
}

export const validateRequiredCustomFields = (customFieldsValues: Record<number, any>, customFieldsList: any[]) => {
  const requiredFields = (customFieldsList || []).filter(
    (f: any) => f?.owner === 'ORDER' && f?.isActive && f?.isRequired
  )

  const errors: string[] = []
  for (const f of requiredFields) {
    const fieldId = String(f.id)
    const val = customFieldsValues?.[fieldId as any] ?? customFieldsValues?.[Number(fieldId)]
    if (isEmptyRequiredValue(f.input_type, val)) {
      errors.push(`${f.display_name} is required`)
    }
  }
  return errors
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const URL_REGEX = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/

export const validateCustomFieldFormats = (customFieldsValues: Record<number, any>, customFieldsList: any[]) => {
  const activeFields = (customFieldsList || []).filter(
    (f: any) => f?.owner === 'ORDER' && f?.isActive && (f.input_type === 'EMAIL' || f.input_type === 'URL')
  )

  const errors: string[] = []
  for (const f of activeFields) {
    const fieldId = String(f.id)
    let val = customFieldsValues?.[fieldId as any] ?? customFieldsValues?.[Number(fieldId)]
    
    if (typeof val === 'string' && val.trim() !== '') {
      val = val.trim()
      if (f.input_type === 'EMAIL' && !EMAIL_REGEX.test(val)) {
        errors.push(`"${f.display_name}" must be a valid email address`)
      } else if (f.input_type === 'URL' && !URL_REGEX.test(val)) {
        errors.push(`"${f.display_name}" must be a valid URL`)
      }
    }
  }
  return errors
}

export const processDuplicateLineItem = (
  lineItems: any[],
  opt: any,
  itemId: string,
  setGlowingRowId: (id: string | null) => void,
  setOpenVariantRow: (id: string | null) => void,
  setLineItems: any // using any for React.Dispatch to avoid importing types here
): boolean => {
  const duplicateItem = lineItems.find((item) => 
    item.id !== itemId && 
    ((opt.value != null && item.platform_product_variant === opt.value) || 
     (opt.value == null && opt.platform_product != null && item.platform_product === opt.platform_product))
  )

  if (duplicateItem) {
    setGlowingRowId(duplicateItem.id)
    setTimeout(() => setGlowingRowId(null), 2500)
    setOpenVariantRow(null)

    setLineItems((prev: any[]) => {
      const currentItem = prev.find((i) => i.id === itemId)
      if (currentItem && !currentItem.platform_product_variant && !currentItem.platform_product) {
        return prev.filter((i) => i.id !== itemId)
      }
      return prev
    })
    return true
  }
  
  return false
}

